import { useNavigate } from 'react-router-dom'
import { isNil } from 'lodash'
import { DateTime } from 'luxon'
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons'
import { faBank } from '@fortawesome/pro-solid-svg-icons'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { PracticeProfileEntry } from './PracticeProfileEntry'
import { Text, Icon } from '@/ui'

import {
  getTaxEntityName,
  FILING_STATUS_COPY,
  TAX_ENTITY_TYPES,
} from '@/features/taxes/taxConstants'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { TextColors, TypographyStyles } from '@/styles'
import { PriorYearTaxReturnUploadItem } from './shared/PriorYearTaxReturnUploadItem'

const getCurrencyDisplay = (valInCents: number | null | undefined) =>
  isNil(valInCents) ? null : formatCurrencyFromCents(valInCents)

export const TaxInformationSection = () => {
  const navigate = useNavigate()
  const fp = useReselector(getFinancialProfile)
  const taxYear = DateTime.now().year

  const displaySpouse = Boolean(
    fp?.filingStatus &&
    ['married_filing_jointly', 'married_filing_separately'].includes(
      fp?.filingStatus
    )
  )

  const isScorp = fp?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s
  return (
    <div className="tax-information-section">
      <div className="flex flex-row gap-3 w-full justify-between items-center">
        <div className="flex flex-row gap-3 items-center mb-6">
          <Icon
            icon={faBank}
            backgroundStyle="rounded"
            backgroundColor="emphasis"
            size="sm"
          />
          <span>
            <Text variant={TypographyStyles.LABEL_MD} className="mb-2">
              {taxYear} Tax Information
            </Text>
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              Last Updated on{' '}
              {fp?.updatedAt
                ? `${DateTime.fromISO(fp.updatedAt).toLocaleString(DateTime.DATETIME_MED)}`
                : ''}
            </Text>
          </span>
        </div>

        <Icon
          className="mb-6"
          icon={faPenToSquare}
          onClick={() => navigate('/practice/profile/editTax')}
        />
      </div>

      <PracticeProfileEntry
        label="Filing Status"
        value={fp?.filingStatus && FILING_STATUS_COPY[fp?.filingStatus]?.title}
      />
      <PracticeProfileEntry
        label="Tax Entity Type"
        value={getTaxEntityName(fp?.taxEntityType)}
      />
      <PracticeProfileEntry
        label="Deduction Type"
        value={fp?.itemizedDeduction ? 'Itemized' : 'Standard'}
      />
      <PracticeProfileEntry label="Dependents" value={fp?.numberOfDependents} />
      {isScorp && (
        <PracticeProfileEntry
          label="YTD W-2 Private Practice"
          value={getCurrencyDisplay(fp?.scorpPracticeWIncomeInCents)}
        />
      )}

      <PracticeProfileEntry
        label="YTD W-2 Income"
        value={getCurrencyDisplay(fp?.wIncomeInCents)}
      />
      <PracticeProfileEntry
        label="YTD Federal Withholding"
        value={getCurrencyDisplay(fp?.federalWithholdingInCents)}
      />
      <PracticeProfileEntry
        label="YTD State Withholding"
        value={getCurrencyDisplay(fp?.stateWithholdingInCents)}
      />
      <PracticeProfileEntry
        label="YTD 1099 Income"
        value={getCurrencyDisplay(fp?.otherIndividualIncomeInCents)}
      />

      {displaySpouse && (
        <>
          <PracticeProfileEntry
            label="Spouse Name"
            value={
              fp?.spouseFirstName &&
              `${fp?.spouseFirstName} ${fp?.spouseLastName}`
            }
          />

          <PracticeProfileEntry
            label="Spouse YTD W-2 Income"
            value={getCurrencyDisplay(fp?.spouseWIncomeInCents)}
          />
          <PracticeProfileEntry
            label="Spouse YTD Federal Withholding"
            value={getCurrencyDisplay(fp?.spouseFederalWithholdingInCents)}
          />
          <PracticeProfileEntry
            label="Spouse YTD State Withholding"
            value={getCurrencyDisplay(fp?.spouseStateWithholdingInCents)}
          />
          <PracticeProfileEntry
            label="Spouse YTD 1099"
            value={getCurrencyDisplay(fp?.spouseIndividualIncomeInCents)}
          />
        </>
      )}

      <PracticeProfileEntry
        label="Prior Year Tax Return"
        documentCat={UserDocumentCategoryIdentifier.finalReturn}
        taxYear={(taxYear - 1).toString()}
        uploadItem={<PriorYearTaxReturnUploadItem uploadButtonOnly />}
      />
    </div>
  )
}
